/**
 * Loom onboarding step manager.
 *
 * Single source of truth for "which screens does this user see, in what
 * order?". Everything variant- or user-specific about the flow lives here.
 *
 * ─── How to edit ────────────────────────────────────────────────────────
 *  - Add / reorder screens in a variant:  edit `STEPS_BY_VARIANT`.
 *  - Branch on user data (persona, cloud
 *    state, feature flags, etc.):         extend `useStepsForUser` — that's
 *                                         the only place path resolution
 *                                         happens.
 *  - Add a new step surface entirely:     add the constant and component
 *                                         map in `./types.ts`, then
 *                                         reference the constant from any
 *                                         variant list below.
 *
 * The view (`loom-onboarding-view.tsx`) knows nothing about which steps
 * exist, what order they run in, or which experiment gates them — it just
 * renders `<StepManager />`.
 */

import { Fragment, useCallback, useMemo, useState } from 'react';

import { GradientBackground } from '../components/gradient-background';

import { useLoomOnboardingExperiment, LoomOnboardingVariants } from './is-exp-loom-onboarding';
import { ONBOARDING_STEP_COMPONENTS, type OnboardingStep } from './types';

/**
 * Ordered list of steps a user sees, per experiment variant. Order in each
 * array IS the flow order.
 *
 * `CONTROL` intentionally has no steps — a control user shouldn't reach the
 * onboarding view at all (see `product-view-switch.tsx`); the empty list
 * makes the fallback render nothing instead of crashing if it does.
 */
const STEPS_BY_VARIANT: Record<LoomOnboardingVariants, readonly OnboardingStep[]> = {
	[LoomOnboardingVariants.VARIANT_1]: ['persona-use-case-step', 'download-options-step'],
	[LoomOnboardingVariants.VARIANT_2]: [
		'welcome-step',
		'persona-use-case-step',
		'download-options-step',
	],
	[LoomOnboardingVariants.CONTROL]: [],
};

/**
 * Resolves the ordered list of steps for the current user.
 *
 * Right now this only branches on the experiment variant. To also gate on
 * user data extend this one hook.
 */
const useStepsForUser = (): readonly OnboardingStep[] => {
	const { loomOnboardingToWacVariant } = useLoomOnboardingExperiment();
	return useMemo(
		() => (loomOnboardingToWacVariant ? STEPS_BY_VARIANT[loomOnboardingToWacVariant] : []),
		[loomOnboardingToWacVariant],
	);
};

type StepManagerProps = {
	/**
	 * Called when the user continues from the last step. The step manager is
	 * agnostic to what this does — the onboardingview owns cloud-status / redirect
	 * logic.
	 */
	readonly handleContinue: () => void;
};

/**
 * Renders the progress bar and the component for whichever step the user is
 * currently on.
 * Owns the "where am I in the flow?" state; the view above
 * owns everything else (errors, redirects, cloud status, telemetry).
 */
export const StepManager = ({ handleContinue }: StepManagerProps) => {
	const steps = useStepsForUser();
	const [index, setIndex] = useState(0);

	const onContinue = useCallback(() => {
		if (index >= steps.length - 1) {
			handleContinue();
			return;
		}
		setIndex((current) => current + 1);
	}, [handleContinue, index, steps.length]);

	if (steps.length === 0) {
		// Defensive: `product-view-switch` should never route a stepless
		// variant here, but if it does we'd rather render nothing than an
		// empty shell with a broken progress bar.
		return null;
	}

	// `steps.length === 0` is guarded above, so `steps[0]` is always defined here.
	const currentStep = (steps[index] ?? steps[0]) as OnboardingStep;
	const StepComponent = ONBOARDING_STEP_COMPONENTS[currentStep];

	return (
		<Fragment>
			<GradientBackground>
				<StepComponent onContinue={onContinue} />
			</GradientBackground>
		</Fragment>
	);
};
